/**
 * Potential Scalability Issue that needs to be addressed in the future
 * selectedEdgeIdsUser is extracted from CanvasContext, causing forced rerenders to all the edges when only one is selected
 * Can move isSelected to ReactFlow's edge.data property or use Zustand to let edges subscribe only to their own IDs
 */

import React, { useState, useMemo, useCallback } from 'react';
import { getBezierPath, getSmoothStepPath, useStore } from 'reactflow';
import { useEditorUI } from '../../../context/EditorUIContext';
import styles from './styles/Edge.module.css';
import { edgeKeyRoles } from './keys/keyGraph';
import { KEY_VISUALS, DIM_OPACITY } from './keys/keyVisuals';
import { useKeyHighlight } from './keys/KeyHighlightContext';

const textMeasurementCache = new Map();

export const getEllipsizedText = (text, maxWidth, ctx) => {
    if (!text) return text;
    
    const cacheKey = `${text}-${maxWidth}`;
    if (textMeasurementCache.has(cacheKey)) {
        return textMeasurementCache.get(cacheKey);
    }

    const ellipsis = "...";
    let truncatedText = text;
    
    if (!ctx || typeof ctx.measureText !== 'function') {
        console.warn("Canvas context not available for text measurement.");
        return text; 
    }

    try {
        let textWidth = ctx.measureText(truncatedText).width;
        
        if (textWidth <= maxWidth) {
            textMeasurementCache.set(cacheKey, text);
            return text;
        }

        while (textWidth > maxWidth && truncatedText.length > 0) {
            truncatedText = truncatedText.slice(0, -1);
            textWidth = ctx.measureText(truncatedText + ellipsis).width;
        }
        
        const result = truncatedText.length < text.length ? truncatedText + ellipsis : text;
        textMeasurementCache.set(cacheKey, result);
        return result;
    } catch (error) {
        console.error("Error measuring text:", error);
        return text; 
    }
};

const Edge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: propSourcePosition,
    targetPosition: propTargetPosition,
    source, 
    target, 
    data, 
}) => {
    const { selectItem, selectedEdgeIdsUser } = useEditorUI();
    const[hoveredEdgeId, setHoveredEdgeId] = useState(null);

    const sourcePosition = useStore(useCallback((s) => {
        return s.nodeInternals.get(source)?.data?.sourcePosition || propSourcePosition || 'right';
    }, [source, propSourcePosition]));

    const targetPosition = useStore(useCallback((s) => {
        return s.nodeInternals.get(target)?.data?.targetPosition || propTargetPosition || 'left';
    }, [target, propTargetPosition]));
    
    const isSelected = useMemo(() => {
        if (!selectedEdgeIdsUser) return false;
        return Object.values(selectedEdgeIdsUser).some(node => node === id);
    }, [selectedEdgeIdsUser, id]);
    
    const isLoopEdge = source === target;
    
    const [edgePath] = useMemo(() => {
        let path;
        if (isLoopEdge) {
            const radiusY = 50;
            const radiusX = Math.max(20, Math.abs(sourceX - targetX) * 0.6);
            const offset = 5;
            path = `M ${sourceX - offset},${sourceY} A ${radiusX},${radiusY} 0 1,0 ${targetX + offset},${targetY}`;
        } else {
            // [path] = getBezierPath({
            //     sourceX,
            //     sourceY,
            //     sourcePosition,
            //     targetX,
            //     targetY,
            //     targetPosition,
            // });
            [path] = getSmoothStepPath({
                sourceX,
                sourceY,
                sourcePosition,
                targetX,
                targetY,
                targetPosition,
                borderRadius: 20
            });
        }
        return [path]; 
    },[sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, source, target]);

    const handleMouseEnter = useCallback(() => setHoveredEdgeId(id), [id]); 
    const handleMouseLeave = useCallback(() => setHoveredEdgeId(null),[]);
    const handleEdgeClick = useCallback(async (event) => { 
        event.stopPropagation();
        selectItem('choice', id);
    }, [selectItem, id]);

    const isHovered = hoveredEdgeId === id;

    // A choice gated on a key is drawn in that gate's colour and dash pattern,
    // so "needs the key" and "hidden once you have it" read differently.
    const { gate } = edgeKeyRoles(data);
    const keyHighlight = useKeyHighlight();
    const isDimmed = !!keyHighlight && !keyHighlight.edgeIds.has(String(id));

    const edgeStyle = useMemo(() => ({
        stroke: isSelected
            ? '#006666'
            : isHovered
                ? '#999'
                : gate
                    ? KEY_VISUALS[gate].color
                    : '#000',
        strokeWidth: isSelected || isHovered ? 2 : gate ? 1.5 : 1,
        fill: 'none',
        cursor: 'pointer',
        strokeDasharray: gate ? KEY_VISUALS[gate].dash : undefined,
    }), [isSelected, isHovered, gate]);

    const markerId = `arrow-${id}`; 

    return (
        <g
            className={styles.edgePathGroup}
            style={{ opacity: isDimmed ? DIM_OPACITY : 1, transition: 'opacity 0.2s ease' }}
        >
            <defs>
                <marker
                    id={markerId}
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                    markerUnits="strokeWidth" 
                >
                    <path
                        d="M0,0 L10,3.5 L0,7 z"
                        fill={edgeStyle.stroke} 
                    />
                </marker>
            </defs>
            <path
                id={`path-${id}`} 
                d={edgePath}
                style={edgeStyle}
                className="react-flow__edge-path" 
                markerEnd={`url(#${markerId})`} 
            />
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={15} 
                className="react-flow__edge-interaction"
                onMouseEnter={handleMouseEnter} 
                onMouseLeave={handleMouseLeave}
                onClick={handleEdgeClick}
                style={{ cursor: 'pointer' }}
            />
            {isSelected && !isLoopEdge && (
                <path
                    d={edgePath}
                    fill="none"
                    stroke="#00ff00" 
                    strokeWidth={2}
                    strokeOpacity={0.7}
                    strokeDasharray="5 5"
                    style={{ pointerEvents: 'none' }}
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        values="10;0" 
                        dur="0.5s"
                        repeatCount="indefinite"
                    />
                </path>
            )}
        </g>
    );
};

export default React.memo(Edge);